import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, MessageCircle, ArrowLeft } from "lucide-react";
import { toWhatsAppNumber } from "@/lib/utils";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FREQ = { weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual" };

export default function ReciboNomina() {
  const { lineId } = useParams();
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get(`/payroll/receipt/${lineId}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [lineId]);

  if (!data) return <div className="p-10 text-center text-stone-500">Cargando recibo…</div>;
  if (data.error) return <div className="p-10 text-center text-red-600">Recibo no encontrado</div>;

  const { line, period, employee, company } = data;
  const daysWithIn = new Set((data.attendance || []).filter((a) => a.kind === "in").map((a) => a.movement_date)).size;

  const waMessage = `*Recibo de nómina · ${period.folio}*
Empleado: ${line.employee_name}
Período: ${period.start_date} → ${period.end_date}
Días trabajados: ${line.days_worked}

PERCEPCIONES
· Ordinario: ${money(line.ordinary)}
· Horas extra: ${money(line.extra_hours_amount)}
· Otras: ${money(line.extra_perceptions)}
_Total bruto: ${money(line.gross_perceptions)}_

DEDUCCIONES
· ISR: ${money(line.isr)}
· IMSS: ${money(line.imss)}
· INFONAVIT: ${money(line.infonavit)}
· Faltas: ${money(line.absence_deduction)}
· Otras: ${money(line.extra_deductions)}${(line.loan_breakdown || []).length > 0 ? `
${line.loan_breakdown.map((lb) => `· ${lb.description}: ${money(lb.amount)}`).join("\n")}` : ""}
_Total deducciones: ${money(line.total_deductions)}_

*NETO A PAGAR: ${money(line.net_pay)}*
${company.name}`;

  const waLink = phone ? `https://wa.me/${toWhatsAppNumber(phone)}?text=${encodeURIComponent(waMessage)}` : "";

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950">
      {/* Toolbar (non-print) */}
      <div className="print:hidden bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 p-4 flex items-center justify-between flex-wrap gap-3">
        <Button variant="outline" onClick={() => window.close() || window.history.back()}><ArrowLeft className="w-4 h-4 mr-1" /> Cerrar</Button>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="tel"
            placeholder="Tel. WhatsApp (10 dígitos)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-9 px-3 rounded-md border border-stone-300 text-sm w-52"
            data-testid="wa-phone-input"
          />
          <a
            href={waLink}
            target={waLink ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={(e) => !phone && e.preventDefault()}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-white text-sm font-medium ${phone ? "bg-emerald-600 hover:bg-emerald-700" : "bg-stone-300 cursor-not-allowed"}`}
            data-testid="wa-share-btn"
          >
            <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
          </a>
          <Button onClick={() => window.print()} data-testid="print-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Printer className="w-4 h-4 mr-1" /> Imprimir / PDF</Button>
        </div>
      </div>

      {/* Recibo imprimible */}
      <div className="max-w-3xl mx-auto p-6 lg:p-10 print:p-0" data-testid="recibo-content">
        <div className="bg-white dark:bg-stone-950 print:bg-white shadow-sm print:shadow-none border border-stone-200 print:border-0 p-8 print:p-6">
          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b-2 border-[#1E3A8A]">
            <div>
              <div className="text-2xl font-outfit font-bold text-[#1E3A8A]">{company.name}</div>
              <div className="text-xs text-stone-600">{company.address}</div>
              <div className="text-xs text-stone-500 mt-1">RFC: {company.rfc}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500">Recibo de nómina</div>
              <div className="text-lg font-outfit font-bold text-stone-900 dark:text-stone-100">{period.folio}</div>
              <div className="text-xs text-stone-500">Emitido {new Date(data.generated_at).toLocaleDateString("es-MX")}</div>
            </div>
          </div>

          {/* Employee + period */}
          <div className="grid grid-cols-2 gap-6 mt-5">
            <div>
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 mb-1.5">Empleado</div>
              <div className="text-base font-outfit font-bold">{employee?.name}</div>
              <div className="text-xs text-stone-600 mt-1">{employee?.position || ""}{employee?.area ? ` · ${employee.area}` : ""}</div>
              {employee?.rfc && <div className="text-xs text-stone-500 mt-1">RFC: {employee.rfc}</div>}
              {employee?.nss && <div className="text-xs text-stone-500">NSS: {employee.nss}</div>}
              {employee?.curp && <div className="text-xs text-stone-500">CURP: {employee.curp}</div>}
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 mb-1.5">Período</div>
              <div className="text-sm font-outfit font-semibold">{period.start_date} → {period.end_date}</div>
              <div className="text-xs text-stone-600 mt-1">{FREQ[period.period_type]}</div>
              <div className="text-xs text-stone-500 mt-1">Fecha de pago: {period.payment_date || "—"}</div>
              <div className="text-xs text-stone-500">Días trabajados: <b>{line.days_worked}</b> · Días con registro: {daysWithIn}</div>
            </div>
          </div>

          {/* Detail: Perceptions and Deductions */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="border border-emerald-200 rounded-lg p-4">
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-bold text-emerald-800 border-b border-emerald-200 pb-2 mb-2">Percepciones</div>
              <Row label="Sueldo ordinario" value={line.ordinary} />
              <Row label={`Horas extra (${line.extra_hours || 0} h)`} value={line.extra_hours_amount} />
              <Row label="Otras percepciones" value={line.extra_perceptions} />
              <div className="border-t border-emerald-300 mt-2 pt-2">
                <Row label="Total percepciones" value={line.gross_perceptions} bold />
              </div>
            </div>
            <div className="border border-amber-200 rounded-lg p-4">
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-bold text-amber-800 border-b border-amber-200 pb-2 mb-2">Deducciones</div>
              <Row label="ISR" value={line.isr} />
              <Row label="IMSS obrero" value={line.imss} />
              <Row label="INFONAVIT" value={line.infonavit} />
              <Row label={`Descuento faltas (${line.absences || 0} d)`} value={line.absence_deduction} />
              <Row label="Otras deducciones" value={line.extra_deductions} />
              {(line.loan_breakdown || []).map((lb) => (
                <Row key={lb.loan_id} label={lb.description} value={lb.amount} />
              ))}
              <div className="border-t border-amber-300 mt-2 pt-2">
                <Row label="Total deducciones" value={line.total_deductions} bold />
              </div>
            </div>
          </div>

          {/* Net pay */}
          <div className="mt-6 p-5 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-outfit font-semibold opacity-80">Neto a pagar</div>
              <div className="text-[10px] opacity-70 mt-0.5">Vía {employee?.payment_method || "transferencia"}{employee?.bank_account ? ` · ···${(employee.bank_account || "").slice(-4)}` : ""}</div>
            </div>
            <div className="text-3xl font-outfit font-bold tabular-nums">{money(line.net_pay)}</div>
          </div>

          {/* Signature area */}
          <div className="mt-10 grid grid-cols-2 gap-8">
            <div>
              <div className="border-t border-stone-400 pt-2 text-center text-xs text-stone-600">Firma del empleado</div>
              <div className="text-center text-[10px] text-stone-500 mt-1">{employee?.name}</div>
            </div>
            <div>
              <div className="border-t border-stone-400 pt-2 text-center text-xs text-stone-600">Firma del patrón</div>
              <div className="text-center text-[10px] text-stone-500 mt-1">{company.name}</div>
            </div>
          </div>

          {line.notes && (
            <div className="mt-6 p-3 rounded bg-stone-50 dark:bg-stone-900 border border-stone-200 text-xs">
              <span className="text-stone-500 uppercase tracking-wider font-outfit font-semibold">Notas: </span>{line.notes}
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-stone-200 text-[9px] text-center text-stone-400">
            Recibo interno · No es CFDI · Generado por sistema ERP LácteoPro el {new Date(data.generated_at).toLocaleString("es-MX")}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: letter; margin: 1cm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between py-1 text-sm ${bold ? "font-outfit font-bold" : ""}`}>
    <span className={bold ? "text-stone-800" : "text-stone-600"}>{label}</span>
    <span className={`tabular-nums ${bold ? "text-stone-900" : "text-stone-700"}`}>{money(value)}</span>
  </div>
);
